import { fetchEventSource } from "@microsoft/fetch-event-source";
import {
  CanvasEditBodySchema,
  EditCanvasResponseSchema,
  RecommendationEventHandlers,
  CanvasEditBody,
} from "./schema";
import { z } from "zod";

/**
 * Streams canvas edit suggestions from the ZSL service, providing real-time updates
 * as the suggestions are generated.
 *
 * @param input - The input configuration for the canvas edit
 * @param input.prompt - The user's prompt describing the desired changes
 * @param input.canvas - The current canvas state
 * @param input.sourceNodeId - ID of the node initiating the edit
 * @param input.baseUrl - Optional override for the API base URL
 *
 * @param handlers - Callback functions for handling the streaming response
 * @param handlers.onPartial - Called when partial results are available
 * @param handlers.onComplete - Called when complete results are available
 * @param handlers.onError - Called when an error occurs
 *
 * @param signal - Optional AbortSignal to cancel the stream
 *
 * @example
 * ```typescript
 * await streamCanvasEdit(
 *   {
 *     prompt: "Create a new interface for user authentication",
 *     canvas: currentCanvas,
 *     sourceNodeId: "node-1",
 *   },
 *   {
 *     onPartial: (data) => {
 *       // Handle partial updates (e.g., show typing indicators)
 *       console.log('Partial update:', data);
 *     },
 *     onComplete: (data) => {
 *       // Handle complete updates (e.g., apply changes to canvas)
 *       console.log('Complete update:', data);
 *     },
 *     onError: (error) => {
 *       // Handle any errors
 *       console.error('Error:', error);
 *     }
 *   }
 * );
 * ```
 */
export async function streamCanvasEdit(
  input: CanvasEditBody,
  handlers: RecommendationEventHandlers,
  signal?: AbortSignal,
): Promise<void> {
  const validatedInput = CanvasEditBodySchema.parse(input);
  const { baseUrl, ...predictionInput } = validatedInput;

  let lastValidatedData: z.infer<typeof EditCanvasResponseSchema>;

  try {
    await fetchEventSource(`/api/zsl/canvas/edit`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(baseUrl && { "z-override-base-url": baseUrl }),
      },
      body: JSON.stringify(predictionInput),
      openWhenHidden: true,
      signal,
      onmessage(msg) {
        try {
          const data = JSON.parse(msg.data);

          if (data.error) {
            handlers.onError?.(data);
            return;
          }

          lastValidatedData = EditCanvasResponseSchema.parse(data);
          handlers.onPartial?.(lastValidatedData);
        } catch (error) {
          handlers.onError?.({
            error:
              error instanceof Error ? error.message : "Invalid message data",
          });
        }
      },
      onclose() {
        handlers.onComplete?.(lastValidatedData);
      },
      onerror(err) {
        handlers.onError?.({ error: err.message });
      },
    });
  } catch (error) {
    if (error instanceof Error && error.name === "AbortError") {
      return;
    }
    handlers.onError?.({
      error:
        error instanceof Error ? error.message : "Stream processing failed",
    });
  }
}
